import { cn } from "utils/helpers";
import { memo, useCallback } from "react";

export type ColorButtonProps = {
  color?: string;
  active?: boolean;
  onColorChange?: (color: string) => void; // eslint-disable-line no-unused-vars
  /** Renders a "no colour" swatch (diagonal slash over white) instead of a solid colour, and
   * calls `onColorChange` with an empty string on click rather than `color`. Used where "no
   * background" is a meaningful, permanent choice (e.g. table cell/row/column background). */
  isClearSwatch?: boolean;
};

export const ColorButton = memo(({ color, active, onColorChange, isClearSwatch }: ColorButtonProps) => {
  const wrapperClassName = cn(
    "flex items-center justify-center px-1.5 py-1.5 rounded group",
    !active && "hover:bg-neutral-100",
    active && "bg-neutral-100"
  );
  const bubbleClassName = cn(
    "w-4 h-4 rounded bg-slate-100 shadow-sm ring-offset-2 ring-current overflow-hidden",
    !active && `hover:ring-1`,
    active && `ring-1`
  );

  const handleClick = useCallback(() => {
    if (onColorChange) {
      onColorChange(isClearSwatch ? "" : color || "");
    }
  }, [onColorChange, color, isClearSwatch]);

  const bubbleStyle = isClearSwatch
    ? {
        backgroundColor: "#ffffff",
        backgroundImage:
          "linear-gradient(to top right, transparent calc(50% - 1px), #ef4444 calc(50% - 1px), #ef4444 calc(50% + 1px), transparent calc(50% + 1px))",
      }
    : { backgroundColor: color, color: color };

  return (
    <button
      onClick={handleClick}
      className={wrapperClassName}
      title={isClearSwatch ? "No colour" : undefined}
    >
      <div style={bubbleStyle} className={bubbleClassName}></div>
    </button>
  );
});

ColorButton.displayName = "ColorButton";
